import { SocialAuthButtons } from "@/components/auth/common/social-auth-buttons";
import { OTPForm } from "@/components/auth/otp/otp-form";
import { SignupForm } from "@/components/auth/signup/signup-form";
import { LineLabel } from "@/components/common/line-label";
import { BuyerProfileFields } from "@/components/listings/buyer-profile-fields";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Icon } from "@/components/ui/icon";
import { Input } from "@/components/ui/input";
import { AlertTriangleIcon } from "lucide-react";
import type { useListingInquiryDialog } from "./use-listing-inquiry-dialog";

export function InquiryAuthPanels({
  model,
}: {
  model: ReturnType<typeof useListingInquiryDialog>;
}) {
  const {
    step,
    t,
    checkEmailMutation,
    createAccountMutation,
    sendLoginOTPMutation,
    emailForm,
    onEmailSubmit,
    savePendingAndPersist,
    email,
    otpKind,
    onOTPDone,
    user,
    onProfileDone,
    buyerProfile,
    setBuyerProfile,
    submitInquiry,
  } = model;
  return (
    <>
      {step === "auth_start" && (
        <div className="flex flex-col gap-5 rounded-xl border border-border bg-card p-4 sm:border-0 sm:bg-transparent sm:p-0">
          <p className="text-sm text-muted-foreground text-pretty">
            Sign in or create an account to send your inquiry.
          </p>

          <Form {...emailForm}>
            <form
              onSubmit={emailForm.handleSubmit(onEmailSubmit)}
              className="space-y-4"
            >
              {emailForm.formState.isSubmitted &&
                emailForm.formState.errors.root?.message && (
                  <Alert variant="destructive">
                    <AlertTriangleIcon className="size-6" />
                    <AlertDescription>
                      {emailForm.formState.errors.root.message}
                    </AlertDescription>
                  </Alert>
                )}

              <FormField
                control={emailForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("saas.auth.home.form.fields.email.label")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={t(
                          "saas.auth.home.form.fields.email.placeholder",
                        )}
                        autoComplete="email"
                        autoFocus
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                className="h-12 w-full bg-foreground text-background hover:bg-foreground/90"
                type="submit"
                disabled={
                  emailForm.formState.isSubmitting ||
                  checkEmailMutation.isPending ||
                  createAccountMutation.isPending ||
                  sendLoginOTPMutation.isPending
                }
              >
                Continue
              </Button>
            </form>
          </Form>

          <LineLabel>
            <div className="bg-background px-2 text-muted-foreground">
              {t("saas.auth.social.or")}
            </div>
          </LineLabel>
          <div onClick={savePendingAndPersist} onKeyDown={undefined}>
            <SocialAuthButtons />
          </div>

          <p className="text-xs text-muted-foreground text-center">
            No password needed. We&apos;ll verify your email with a quick code.
          </p>
        </div>
      )}

      {step === "auth_otp" && (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">
            We sent a verification code to{" "}
            <span className="font-medium text-foreground">{email}</span>
          </p>
          <OTPForm
            identifier={email.trim().toLowerCase()}
            kind={otpKind}
            callbackUrl="/"
            onDone={onOTPDone}
          />
        </div>
      )}

      {step === "auth_complete_profile" && (
        <SignupForm
          defaultEmail={email || user?.email}
          emailNonEditable
          onDone={onProfileDone}
          isPostOAuth
          requirePhone
          roles={["BUYER"]}
        />
      )}

      {step === "buyer_profile" && (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">
            Help us match you with the right opportunities. These are optional
            and can be changed later.
          </p>
          <BuyerProfileFields data={buyerProfile} onChange={setBuyerProfile} />
          <Button
            className="h-12 w-full bg-foreground text-background hover:bg-foreground/90"
            onClick={() => submitInquiry()}
          >
            <Icon icon="Send" />
            Send Inquiry
          </Button>
          <button
            type="button"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => submitInquiry()}
          >
            Skip for now
          </button>
        </div>
      )}
    </>
  );
}
